import type { ArenaFrame, ArenaPlayer } from '@biplanes/contracts';
import type { Assets } from '../core/assets.js';
import type { Screen } from '../core/screen.js';
import type { AudioSystem } from '../audio/audio.js';
import type { ArenaClient } from '../net/arena.js';
import { KeyboardController, TouchController } from '../sim/controllers.js';
import { createSim } from '../sim/sim.js';
import { SeededRandom } from '../sim/rng.js';
import { inputMask } from '../net/input.js';

export class ArenaScene {
  private readonly keyboard = new KeyboardController();
  private readonly touch = new TouchController();
  private readonly inputState = createSim(1);
  private readonly rng = new SeededRandom(1);
  private frame: ArenaFrame | null = null;
  private showScores = false;
  private paused = false;
  private panel: HTMLDivElement | null = null;
  private lastBodies = new Map<number, number>();
  private readonly positions = new Map<number, { x: number; y: number; px: number; py: number }>();
  private readonly key = (event: KeyboardEvent): void => {
    if (event.key === 'Tab') {
      event.preventDefault();
      this.showScores = event.type === 'keydown';
    }
    if (event.type === 'keydown' && !event.repeat && ['Escape', 'p', 'P'].includes(event.key)) {
      event.preventDefault();
      this.toggleMenu();
    }
  };
  constructor(
    private readonly screen: Screen,
    private readonly assets: Assets,
    private readonly audio: AudioSystem,
    private readonly client: ArenaClient,
    private readonly onExit: () => void,
  ) {}
  attach(): void {
    this.keyboard.attach();
    this.touch.attach();
    window.addEventListener('keydown', this.key);
    window.addEventListener('keyup', this.key);
  }
  detach(): void {
    this.keyboard.detach();
    this.touch.detach();
    window.removeEventListener('keydown', this.key);
    window.removeEventListener('keyup', this.key);
    this.panel?.remove();
    this.client.close();
    this.client.free();
  }
  private toggleMenu(): void {
    this.paused = !this.paused;
    this.keyboard.clear();
    this.touch.clear();
    if (!this.paused) {
      this.panel?.remove();
      this.panel = null;
      return;
    }
    const panel = document.createElement('div');
    this.panel = panel;
    Object.assign(panel.style, {
      position: 'fixed',
      inset: '25%',
      zIndex: '10',
      background: '#eee8d1',
      color: '#203846',
      padding: '20px',
      fontFamily: 'monospace',
      touchAction: 'auto',
    });
    const text = document.createElement('p');
    text.textContent = 'The arena keeps flying while this menu is open.';
    panel.append(text);
    for (const [label, action] of [
      ['Resume', () => this.toggleMenu()],
      ['Leave arena', this.onExit],
    ] as const) {
      const button = document.createElement('button');
      button.textContent = label;
      button.addEventListener('click', action);
      panel.append(button);
    }
    document.body.append(panel);
  }
  update(): void {
    let mask = 0;
    if (!this.paused)
      mask =
        inputMask(this.keyboard.poll(this.inputState, 0, this.rng)) |
        inputMask(this.touch.poll(this.inputState, 0, this.rng));
    this.client.step(mask);
  }
  diagnostics(): Record<string, unknown> {
    return {
      scene: 'arena',
      tick: this.frame?.tick ?? 0,
      player: this.frame?.you,
      players: this.frame?.total,
      visible: this.frame?.players.length,
      error: this.client.error(),
    };
  }
  render(_fps: number): void {
    this.client.pump();
    const error = this.client.error();
    const frame = JSON.parse(this.client.frame_json()) as ArenaFrame | null;
    this.screen.clear('#b9dce0');
    if (!frame) {
      this.assets.fontMain.drawCentered(this.screen.ctx, error ?? 'CONNECTING...', 64, 60);
      return;
    }
    this.frame = frame;
    const me = frame.players.find((p) => p.id === frame.you);
    if (!me) return;
    const position = (p: ArenaPlayer): { x: number; y: number } => ({
      x: (p.life === 0 ? p.x : p.pilot_x) / 64,
      y: (p.life === 0 ? p.y : p.pilot_y) / 64,
    });
    const center = position(me);
    const camera = { x: center.x - 64, y: Math.max(0, Math.min(81, center.y - 64)) };
    const ctx = this.screen.ctx;
    const screenX = (x: number): number => {
      let delta = (((x - center.x) % frame.width) + frame.width) % frame.width;
      if (delta > frame.width / 2) delta -= frame.width;
      return Math.round(delta + 64);
    };
    const image = (name: string, x: number, y: number, centered = true): void => {
      const img = this.assets.sprites.get(name);
      if (img)
        ctx.drawImage(
          img,
          Math.round(x - (centered ? img.width / 2 : 0)),
          Math.round(y - (centered ? img.height / 2 : 0)),
        );
    };
    for (
      let sector = Math.floor(camera.x / 256) - 1;
      sector <= Math.floor((camera.x + 128) / 256) + 1;
      sector++
    ) {
      const x = sector * 256 - camera.x;
      ctx.fillStyle = '#587e67';
      ctx.fillRect(Math.round(x), 189 - camera.y, 256, 40);
      image('fx_01', x + 109, 168 - camera.y, false);
    }
    const current = new Set<number>();
    for (const p of frame.players) {
      current.add(p.id);
      const target = { x: p.x / 64, y: p.y / 64, px: p.pilot_x / 64, py: p.pilot_y / 64 };
      const previous = this.positions.get(p.id);
      // Blend remote snapshots; the local player is already predicted in WASM.
      if (previous && p.id !== frame.you) {
        const blend = (old: number, next: number): number => old + (next - old) * 0.4;
        if (Math.abs(target.x - previous.x) < 64 && Math.abs(target.y - previous.y) < 64) {
          target.x = blend(previous.x, target.x);
          target.y = blend(previous.y, target.y);
        }
        if (Math.abs(target.px - previous.px) < 64) {
          target.px = blend(previous.px, target.px);
          target.py = blend(previous.py, target.py);
        }
      }
      this.positions.set(p.id, target);
      const x = screenX(target.x),
        y = Math.round(target.y - camera.y);
      if (p.body < 2)
        image(`plane_${String(p.frame + (p.facing_left ? 0 : 16)).padStart(2, '0')}`, x, y);
      else if (p.body === 2) image(`fx_${String(2 + Math.min(6, p.frame)).padStart(2, '0')}`, x, y);
      if (p.life !== 0) {
        const px = screenX(target.px),
          py = Math.round(target.py - camera.y);
        if (p.life === 2) image('fx_28', px, py - 10);
        image(p.life === 3 ? 'fx_17' : 'fx_21', px, py);
      }
      const label = position(p);
      this.assets.fontMain.drawCentered(
        ctx,
        p.nickname.slice(0, 10).toUpperCase(),
        screenX(label.x),
        label.y - camera.y - 23,
      );
      if (this.lastBodies.get(p.id) === 1 && p.body === 2) this.audio.sfx.crash();
      this.lastBodies.set(p.id, p.body);
    }
    for (const id of this.positions.keys())
      if (!current.has(id)) {
        this.positions.delete(id);
        this.lastBodies.delete(id);
      }
    ctx.fillStyle = '#203846';
    for (const b of frame.bullets) ctx.fillRect(screenX(b.x), Math.round(b.y - camera.y), 2, 2);
    ctx.fillStyle = '#eee8d1';
    ctx.fillRect(0, 0, 128, 10);
    this.assets.fontMain.draw(ctx, `FFA ${frame.total}  ${me.score} PTS`, 2, 1);
    if (this.showScores) {
      ctx.fillStyle = '#eee8d1';
      ctx.fillRect(2, 12, 124, Math.min(112, frame.leaders.length * 10 + 14));
      this.assets.fontMain.draw(ctx, 'FLIGHT LEADERS', 5, 15);
      frame.leaders.forEach((p, i) =>
        this.assets.fontMain.draw(
          ctx,
          `${p.score} ${p.nickname.slice(0, 12).toUpperCase()}`,
          5,
          27 + i * 10,
        ),
      );
    }
    if (error) {
      ctx.fillStyle = '#eee8d1';
      ctx.fillRect(0, 104, 128, 24);
      this.assets.fontRed
        .wrap(error, 124)
        .slice(0, 2)
        .forEach((line, i) => this.assets.fontRed.draw(ctx, line, 2, 106 + i * 9));
    }
  }
}
